import { useState } from 'react'
import { Row, Col, Card, Button, message, Spin, Modal, Checkbox, Select, Divider } from 'antd'
import {
    DownloadOutlined,
    FileExcelOutlined,
    TeamOutlined,
    MessageOutlined,
    CheckCircleOutlined,
    LoadingOutlined
} from '@ant-design/icons'
import api from '../services/api'

interface ExportItem {
    title: string
    description: string
    icon: React.ReactNode
    color: string
    exportAll: () => Promise<void>
    exportSelected: () => void
}

export default function Export() {
    const [loadingContacts, setLoadingContacts] = useState(false)
    const [loadingChats, setLoadingChats] = useState(false)
    const [contactsModalVisible, setContactsModalVisible] = useState(false)
    const [chatsModalVisible, setChatsModalVisible] = useState(false)
    const [contacts, setContacts] = useState<any[]>([])
    const [chats, setChats] = useState<any[]>([])
    const [selectedContacts, setSelectedContacts] = useState<number[]>([])
    const [selectedChats, setSelectedChats] = useState<number[]>([])
    const [loadingList, setLoadingList] = useState(false)


    const downloadExcel = async (endpoint: string, filename: string, setLoading: (v: boolean) => void) => {
        setLoading(true)
        try {
            const response = await api.get(endpoint, {
                responseType: 'blob'
            })

            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = filename
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)

            message.success('Файл успешно скачан!')
        } catch (error) {
            console.error('Export error:', error)
            message.error('Ошибка экспорта')
        } finally {
            setLoading(false)
        }
    }


    const exportAllContacts = async () => {
        await downloadExcel('/contacts/export/excel?all=true', `contacts_${new Date().toISOString().split('T')[0]}.xlsx`, setLoadingContacts)
    }


    const exportAllChats = async () => {
        await downloadExcel('/chats/export/excel?all=true', `chats_${new Date().toISOString().split('T')[0]}.xlsx`, setLoadingChats)
    }


    const loadContacts = async () => {
        setLoadingList(true)
        try {
            const response = await api.get('/contacts')
            setContacts(response.data.items || response.data)
        } catch (error) {
            message.error('Ошибка загрузки контактов')
        } finally {
            setLoadingList(false)
        }
    }


    const loadChats = async () => {
        setLoadingList(true)
        try {
            const response = await api.get('/chats')
            setChats(response.data.items || response.data)
        } catch (error) {
            message.error('Ошибка загрузки чатов')
        } finally {
            setLoadingList(false)
        }
    }


    const exportSelectedContacts = async () => {
        if (selectedContacts.length === 0) {
            message.warning('Выберите контакты для экспорта')
            return
        }
        const idsParam = selectedContacts.join(',')
        await downloadExcel(`/contacts/export/excel?ids=${idsParam}`, `contacts_selected_${new Date().toISOString().split('T')[0]}.xlsx`, setLoadingContacts)
        message.success(`Экспортировано ${selectedContacts.length} контактов`)
        setContactsModalVisible(false)
        setSelectedContacts([])
    }


    const exportSelectedChats = async () => {
        if (selectedChats.length === 0) {
            message.warning('Выберите чаты для экспорта')
            return
        }
        const idsParam = selectedChats.join(',')
        await downloadExcel(`/chats/export/excel?ids=${idsParam}`, `chats_selected_${new Date().toISOString().split('T')[0]}.xlsx`, setLoadingChats)
        message.success(`Экспортировано ${selectedChats.length} чатов`)
        setChatsModalVisible(false)
        setSelectedChats([])
    }


    const ExportCard = ({
        title,
        description,
        icon,
        color,
        loading,
        onExportAll,
        onExportSelected
    }: {
        title: string
        description: string
        icon: React.ReactNode
        color: string
        loading: boolean
        onExportAll: () => void
        onExportSelected: () => void
    }) => (
        <Card
            style={{
                background: 'var(--bg-surface)',
                borderRadius: 20,
                border: '1px solid var(--border)',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
            }}
            bodyStyle={{ padding: 0 }}
            hoverable
        >

            <div style={{
                background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                padding: 24,
                display: 'flex',
                alignItems: 'center',
                gap: 16
            }}>
                <div style={{
                    width: 60,
                    height: 60,
                    borderRadius: 16,
                    background: 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 28,
                    color: 'white'
                }}>
                    {icon}
                </div>
                <div>
                    <div style={{
                        fontSize: 22,
                        fontWeight: 700,
                        color: 'white',
                        marginBottom: 4
                    }}>
                        {title}
                    </div>
                    <div style={{
                        fontSize: 14,
                        color: 'rgba(255,255,255,0.8)'
                    }}>
                        {description}
                    </div>
                </div>
            </div>


            <div style={{ padding: 20 }}>
                <Button
                    type="primary"
                    icon={loading ? <LoadingOutlined spin /> : <DownloadOutlined />}
                    onClick={onExportAll}
                    disabled={loading}
                    block
                    size="large"
                    style={{
                        background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                        border: 'none',
                        height: 50,
                        borderRadius: 12,
                        fontWeight: 600,
                        fontSize: 15,
                        marginBottom: 12
                    }}
                >
                    {loading ? 'Экспорт...' : 'Выгрузить все'}
                </Button>

                <Button
                    icon={<FileExcelOutlined />}
                    onClick={onExportSelected}
                    block
                    size="large"
                    style={{
                        height: 50,
                        borderRadius: 12,
                        fontWeight: 500,
                        fontSize: 15,
                        borderColor: color,
                        color: color
                    }}
                >
                    Выбрать для экспорта
                </Button>
            </div>
        </Card>
    )

    return (
        <div style={{ padding: 24 }}>

            <div style={{ marginBottom: 32 }}>
                <h1 style={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: 'var(--text-primary)',
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12
                }}>
                    <FileExcelOutlined style={{ color: '#10b981' }} />
                    Выгрузка данных
                </h1>
                <p style={{
                    color: 'var(--text-muted)',
                    margin: '8px 0 0',
                    fontSize: 15
                }}>
                    Экспортируйте контакты и чаты в формат Excel (.xlsx)
                </p>
            </div>


            <Row gutter={[24, 24]}>
                <Col xs={24} sm={12} lg={8}>
                    <ExportCard
                        title="Контакты"
                        description="Экспорт базы контактов"
                        icon={<TeamOutlined />}
                        color="#667eea"
                        loading={loadingContacts}
                        onExportAll={exportAllContacts}
                        onExportSelected={() => {
                            loadContacts()
                            setContactsModalVisible(true)
                        }}
                    />
                </Col>

                <Col xs={24} sm={12} lg={8}>
                    <ExportCard
                        title="Чаты"
                        description="Экспорт списка чатов"
                        icon={<MessageOutlined />}
                        color="#10b981"
                        loading={loadingChats}
                        onExportAll={exportAllChats}
                        onExportSelected={() => {
                            loadChats()
                            setChatsModalVisible(true)
                        }}
                    />
                </Col>
            </Row>


            <Card
                style={{
                    marginTop: 32,
                    background: 'var(--bg-surface)',
                    borderRadius: 16,
                    border: '1px solid var(--border)'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                    <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: 12,
                        background: 'linear-gradient(135deg, #f59e0b20 0%, #f59e0b10 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 24
                    }}>
                        💡
                    </div>
                    <div>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>
                            Подсказка
                        </div>
                        <div style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                            Экспортированные файлы Excel можно открыть в Microsoft Excel, Google Sheets или LibreOffice Calc.
                            Данные включают всю доступную информацию: имена, телефоны, даты, статусы и другие поля.
                        </div>
                    </div>
                </div>
            </Card>


            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <TeamOutlined style={{ color: '#667eea' }} />
                        <span>Выберите контакты для экспорта</span>
                    </div>
                }
                open={contactsModalVisible}
                onCancel={() => {
                    setContactsModalVisible(false)
                    setSelectedContacts([])
                }}
                width={600}
                footer={[
                    <div key="info" style={{ float: 'left', color: 'var(--text-muted)' }}>
                        Выбрано: {selectedContacts.length}
                    </div>,
                    <Button key="cancel" onClick={() => setContactsModalVisible(false)}>
                        Отмена
                    </Button>,
                    <Button
                        key="export"
                        type="primary"
                        icon={<DownloadOutlined />}
                        loading={loadingContacts}
                        onClick={exportSelectedContacts}
                        style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none'
                        }}
                    >
                        Экспортировать
                    </Button>
                ]}
            >
                <Spin spinning={loadingList}>
                    <div style={{ marginBottom: 12 }}>
                        <Checkbox
                            checked={selectedContacts.length === contacts.length && contacts.length > 0}
                            indeterminate={selectedContacts.length > 0 && selectedContacts.length < contacts.length}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    setSelectedContacts(contacts.map(c => c.id))
                                } else {
                                    setSelectedContacts([])
                                }
                            }}
                        >
                            Выбрать все ({contacts.length})
                        </Checkbox>
                    </div>
                    <Divider style={{ margin: '12px 0' }} />
                    <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                        <Checkbox.Group
                            value={selectedContacts}
                            onChange={(values) => setSelectedContacts(values as number[])}
                            style={{ width: '100%' }}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {contacts.map(contact => (
                                    <Checkbox key={contact.id} value={contact.id}>
                                        <span style={{ fontWeight: 500 }}>
                                            {contact.first_name} {contact.last_name}
                                        </span>
                                        {contact.phone && (
                                            <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>
                                                {contact.phone}
                                            </span>
                                        )}
                                    </Checkbox>
                                ))}
                            </div>
                        </Checkbox.Group>
                    </div>
                </Spin>
            </Modal>


            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <MessageOutlined style={{ color: '#10b981' }} />
                        <span>Выберите чаты для экспорта</span>
                    </div>
                }
                open={chatsModalVisible}
                onCancel={() => {
                    setChatsModalVisible(false)
                    setSelectedChats([])
                }}
                width={600}
                footer={[
                    <div key="info" style={{ float: 'left', color: 'var(--text-muted)' }}>
                        Выбрано: {selectedChats.length}
                    </div>,
                    <Button key="cancel" onClick={() => setChatsModalVisible(false)}>
                        Отмена
                    </Button>,
                    <Button
                        key="export"
                        type="primary"
                        icon={<DownloadOutlined />}
                        loading={loadingChats}
                        onClick={exportSelectedChats}
                        style={{
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            border: 'none'
                        }}
                    >
                        Экспортировать
                    </Button>
                ]}
            >
                <Spin spinning={loadingList}>
                    <div style={{ marginBottom: 12 }}>
                        <Checkbox
                            checked={selectedChats.length === chats.length && chats.length > 0}
                            indeterminate={selectedChats.length > 0 && selectedChats.length < chats.length}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    setSelectedChats(chats.map(c => c.id))
                                } else {
                                    setSelectedChats([])
                                }
                            }}
                        >
                            Выбрать все ({chats.length})
                        </Checkbox>
                    </div>
                    <Divider style={{ margin: '12px 0' }} />
                    <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                        <Checkbox.Group
                            value={selectedChats}
                            onChange={(values) => setSelectedChats(values as number[])}
                            style={{ width: '100%' }}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {chats.map(chat => (
                                    <Checkbox key={chat.id} value={chat.id}>
                                        <span style={{ fontWeight: 500 }}>
                                            {chat.title || chat.first_name || `Chat ${chat.telegram_id}`}
                                        </span>
                                        <span style={{
                                            color: 'var(--text-muted)',
                                            marginLeft: 8,
                                            fontSize: 12
                                        }}>
                                            {chat.chat_type}
                                        </span>
                                    </Checkbox>
                                ))}
                            </div>
                        </Checkbox.Group>
                    </div>
                </Spin>
            </Modal>
        </div>
    )
}
